// Serialize scans to pretty JSON and trigger a browser download of `filename`.
// Full fidelity by design: the raw `Scan` objects as a JSON array, including
// `metaJSON`, with no flattening or field transformation. Uses a native Blob +
// programmatic anchor click (no external library), and always revokes the
// object URL so the blob can be garbage-collected.
export function downloadScansAsJson(scans, filename) {
    const json = JSON.stringify(scans, null, 2);
    triggerDownload(json, 'application/json', filename);
}
// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------
//
// The CSV is FLAT (one row per scan, one column per leaf value) and uses a
// FIXED, hardcoded canonical schema: the same columns in the same order on
// every export, regardless of the data present. A value absent or null at any
// level renders as an empty cell; keys outside the schema are dropped. This
// trades fidelity for diffability — the JSON export remains the full-fidelity
// escape hatch. See design.md "Fixed canonical columns over dynamic union".
//
// To widen the schema later (new region, new firmware field), edit the central
// constants below — the header and per-scan projection both derive from them.
// The three skin regions, shared by both `rawData_<region>_*` and
// `metaJSON_<region>_*` so the two sections line up.
const REGIONS = ['forehead', 'left_cheek', 'right_cheek'];
// Top-level scalar columns, by their own field names.
const TOP_LEVEL_COLUMNS = [
    '_id',
    'user',
    'takenAt',
    'photoUrl',
    'scanWindow',
    'createdAt',
    'updatedAt',
];
const ENVIRONMENT_FIELDS = ['humid', 'temp', 'tempUnit'];
const DEVICE_FIELDS = ['serial', 'hwVer', 'fwVer'];
// `rawData` is keyed by each reading's `type` (the region), NOT by array index,
// so columns are stable across rows and align with the metaJSON section.
const RAWDATA_METRICS = ['hydration', 'elasticity', 'oiliness'];
// Per-region metaJSON scalars, then the s1_0..s1_43 and s2_0..s2_43 sensor
// arrays in numeric order (so `s1_2` precedes `s1_10`).
const META_SCALAR_FIELDS = [
    'check_step',
    'datetime',
    'device',
    'elas_freq',
    'fw_ver',
    'hydration',
    'oiliness',
];
const META_SENSOR_FIELDS = [
    ...Array.from({ length: 44 }, (_, i) => `s1_${i}`),
    ...Array.from({ length: 44 }, (_, i) => `s2_${i}`),
];
const META_FIELDS = [...META_SCALAR_FIELDS, ...META_SENSOR_FIELDS];
// The full ordered header, derived once from the constants above:
// top-level → environment_* → device_* → rawData_<region>_* → metaJSON_<region>_*.
export const CSV_COLUMNS = [
    ...TOP_LEVEL_COLUMNS,
    ...ENVIRONMENT_FIELDS.map((f) => `environment_${f}`),
    ...DEVICE_FIELDS.map((f) => `device_${f}`),
    ...REGIONS.flatMap((r) => RAWDATA_METRICS.map((m) => `rawData_${r}_${m}`)),
    ...REGIONS.flatMap((r) => META_FIELDS.map((f) => `metaJSON_${r}_${f}`)),
];
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
// Render one canonical cell: null/undefined → empty string; everything else via
// `String()` to keep full numeric precision (e.g. 32.64801788330078). Never the
// literal text "null" (which would pollute otherwise-numeric columns).
function toCell(value) {
    if (value === null || value === undefined)
        return '';
    return String(value);
}
// Project a scan onto the canonical columns. Defensive at every level — null
// `environment`/`device`, an empty `rawData`, and an absent/untyped `metaJSON`
// all yield empty cells rather than throwing. Keys outside the schema are never
// read, so they cannot appear in the output.
export function scanToFlatRow(scan) {
    const row = {};
    // Top-level scalars (createdAt/updatedAt are optional → may be undefined).
    row._id = toCell(scan._id);
    row.user = toCell(scan.user);
    row.takenAt = toCell(scan.takenAt);
    row.photoUrl = toCell(scan.photoUrl);
    row.scanWindow = toCell(scan.scanWindow);
    row.createdAt = toCell(scan.createdAt);
    row.updatedAt = toCell(scan.updatedAt);
    // environment_* / device_* — nullable objects.
    for (const field of ENVIRONMENT_FIELDS) {
        row[`environment_${field}`] = toCell(scan.environment?.[field]);
    }
    for (const field of DEVICE_FIELDS) {
        row[`device_${field}`] = toCell(scan.device?.[field]);
    }
    // rawData_<region>_* — find the reading whose `type` is this region.
    for (const region of REGIONS) {
        const reading = scan.rawData.find((r) => r.type === region);
        for (const metric of RAWDATA_METRICS) {
            row[`rawData_${region}_${metric}`] = toCell(reading?.[metric]);
        }
    }
    // metaJSON_<region>_<field> — read the untyped blob defensively.
    const meta = isRecord(scan.metaJSON) ? scan.metaJSON : undefined;
    for (const region of REGIONS) {
        const regionBlob = isRecord(meta?.[region]) ? meta[region] : undefined;
        for (const field of META_FIELDS) {
            row[`metaJSON_${region}_${field}`] = toCell(regionBlob?.[field]);
        }
    }
    return row;
}
// Escape one field per RFC 4180: wrap in double quotes when it contains a comma,
// double quote, CR, or LF, doubling any interior double quotes.
function escapeCsvField(value) {
    if (/[",\r\n]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}
// Serialize scans to a CSV string: the fixed canonical header row followed by
// one row per scan, fields CRLF-delimited per RFC 4180. No UTF-8 BOM.
export function scansToCsv(scans) {
    const header = CSV_COLUMNS.map(escapeCsvField).join(',');
    const rows = scans.map((scan) => {
        const flat = scanToFlatRow(scan);
        return CSV_COLUMNS.map((col) => escapeCsvField(flat[col] ?? '')).join(',');
    });
    return [header, ...rows].join('\r\n');
}
// Serialize scans to flat CSV and trigger a browser download of `filename`,
// mirroring `downloadScansAsJson` (Blob + anchor click + revokeObjectURL).
export function downloadScansAsCsv(scans, filename) {
    triggerDownload(scansToCsv(scans), 'text/csv', filename);
}
// Shared client-side download: wrap text in a Blob and click a transient anchor,
// always revoking the object URL so the blob can be garbage-collected.
function triggerDownload(text, mimeType, filename) {
    const blob = new Blob([text], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Release the object URL now that the download has been kicked off.
    URL.revokeObjectURL(url);
}
// Compose `acie-scans-<scope>-<env>-YYYY-MM-DD.<format>` from a scope label
// ('all' for the grid, a userId for the drawer) and the runtime environment.
// The date is the local calendar date (matches what the operator sees); the
// `date` arg is injectable so the format is unit-testable. `format` defaults to
// 'json' so the existing JSON callers are unchanged.
export function scanExportFilename(scope, env, date = new Date(), format = 'json') {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `acie-scans-${scope}-${env}-${year}-${month}-${day}.${format}`;
}
